import React, { useState, useContext, useEffect } from "react";

import AuthContext from "../../context/auth/authContext";
import AlertContext from "../../context/alert/alertContext";

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import LoadingButton from '@mui/lab/LoadingButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Button from '@mui/material/Button';
import PersonIcon from "@material-ui/icons/Person";

const Profile = () => {
  const authContext = useContext(AuthContext);
  const alertContext = useContext(AlertContext);
  const { setAlert } = alertContext;
  const {
    user,
    loadUser,
    update,
    message,
    clearAuthMessages,
    userUpdated,
    updateUserComplete,
  } = authContext;

  const { name, first_name, last_name, avatar } = user;

  const [currentUser, setCurrentUser] = useState({
    name: "",
    first_name: "",
    last_name: "",
    password: "",
    password2: "",
    avatar: null,
  });

  const [isUpdatingUser, setIsUpdatingUser] = useState(false);

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isUpdatingUser && userUpdated) {
      setIsUpdatingUser(false);
      updateUserComplete();
    }
  }, [isUpdatingUser, userUpdated, updateUserComplete]);

  useEffect(() => {
    if (message === "success") {
      setAlert("Profile successfully updated", "success");
      clearAuthMessages();
    }
    // eslint-disable-next-line
  }, [message]);

  useEffect(() => {
    if (first_name !== null || last_name !== null) {
      setCurrentUser({
        ...currentUser,
        name: name || "",
        first_name: first_name || "",
        last_name: last_name || "",
        avatar: avatar,
      });
    } else {
      setCurrentUser({
        first_name: "",
        last_name: "",
        password: "",
        password2: "",
        avatar: null,
      });
    }
    // eslint-disable-next-line
  }, [authContext]);

  const [file, setFile] = useState(null);

  const onSelectFile = (e) => {
    setCurrentUser({
      ...currentUser,
      [e.target.name]: e.target.files[0],
    });
    setFile(e.target.files[0]);
  };

  const onChange = (e) =>
    setCurrentUser({
      ...currentUser,
      [e.target.name]: e.target.value,
    });

  const onSubmit = (e) => {
    e.preventDefault();
    if (currentUser.password && currentUser.password.length < 8) {
      setAlert("Password must be at least 8 characters", "error");
      return;
    }
    if (currentUser.password && currentUser.password !== currentUser.password2) {
      setAlert("Passwords do not match", "error");
      return;
    }
    update({
      name: currentUser.name,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
      password: currentUser.password || undefined,
      avatar: currentUser.avatar,
    });
    setIsUpdatingUser(true);
    setCurrentUser({ ...currentUser, password: "", password2: "" });
  };

  const avatarSrc = file
    ? URL.createObjectURL(file)
    : avatar && avatar.image_link
    ? avatar.image_link
    : null;

  return (
    <Box sx={{ width: '100%', pt: 8 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Profile
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Update your personal information and profile photo.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 560 }}>
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
          <form noValidate autoComplete="off" onSubmit={onSubmit}>
            {/* Avatar section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Avatar
                src={avatarSrc}
                sx={{ width: 120, height: 120, mb: 2, fontSize: 48 }}
              >
                {!avatarSrc && <PersonIcon style={{ fontSize: 48 }} />}
              </Avatar>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                sx={{
                  textTransform: 'none',
                  borderStyle: 'dashed',
                  color: 'text.secondary',
                }}
              >
                {file ? file.name : 'Change photo'}
                <input
                  type="file"
                  accept="image/*"
                  name="avatar"
                  hidden
                  onChange={onSelectFile}
                />
              </Button>
            </Box>

            {/* Form fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                fullWidth
                id="display-name"
                label="Display name"
                name="name"
                variant="outlined"
                size="small"
                value={(currentUser && currentUser.name) || ""}
                onChange={onChange}
              />
              <TextField
                fullWidth
                id="first-name"
                label="First name"
                name="first_name"
                variant="outlined"
                size="small"
                value={(currentUser && currentUser.first_name) || ""}
                onChange={onChange}
              />
              <TextField
                fullWidth
                id="last-name"
                label="Last name"
                name="last_name"
                variant="outlined"
                size="small"
                value={(currentUser && currentUser.last_name) || ""}
                onChange={onChange}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                Change Password
              </Typography>
              <TextField
                fullWidth
                id="new-password"
                label="New password"
                name="password"
                type="password"
                variant="outlined"
                size="small"
                value={(currentUser && currentUser.password) || ""}
                onChange={onChange}
                helperText="Leave blank to keep current password"
              />
              <TextField
                fullWidth
                id="confirm-password"
                label="Confirm new password"
                name="password2"
                type="password"
                variant="outlined"
                size="small"
                value={(currentUser && currentUser.password2) || ""}
                onChange={onChange}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                loading={isUpdatingUser}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.2,
                  borderRadius: 2,
                  mt: 1,
                }}
              >
                Save Changes
              </LoadingButton>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default Profile;
